import { Badge, type BadgeProps } from '@chopchop-se/ui';
import type { ErpInboxStatus, ErpOutboxStatus } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Queue statuses as pills. Tones carry the meaning an operator scans for:
 * amber = still working on it, red = needs a human, green = done.
 */

const OUTBOX_TONE: Record<ErpOutboxStatus, BadgeProps['tone']> = {
  pending: 'info',
  sent: 'success',
  failed: 'warning',
  dead: 'danger',
};

const INBOX_TONE: Record<ErpInboxStatus, BadgeProps['tone']> = {
  pending: 'info',
  applied: 'success',
  skipped: 'neutral',
  failed: 'danger',
};

export function OutboxStatusBadge({ status }: { status: ErpOutboxStatus }): ReactNode {
  const { t } = useTranslation('bcIntegration');
  return (
    <Badge tone={OUTBOX_TONE[status] ?? 'neutral'} dot minWidth>
      {t(`statuses.${status}`)}
    </Badge>
  );
}

export function InboxStatusBadge({ status }: { status: ErpInboxStatus }): ReactNode {
  const { t } = useTranslation('bcIntegration');
  return (
    <Badge tone={INBOX_TONE[status] ?? 'neutral'} dot minWidth>
      {t(`statuses.${status}`)}
    </Badge>
  );
}

/** A monospace JSON block for payload/error inspection in the detail modal. */
export function PayloadBlock({ value }: { value: unknown }): ReactNode {
  return (
    <pre className="max-h-96 overflow-auto rounded-lg bg-surface-subtle p-3 font-mono text-xs leading-relaxed text-ink-light">
      {JSON.stringify(value ?? null, null, 2)}
    </pre>
  );
}
